import { withOpeningAd } from "./ads/app-open";
import { prepareStartupUpdate } from "./game/startup-update";
import "./compat/webview83";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { StartupBoundary, StartupScreen } from "./components/StartupScreen";
import { createBootReadiness, optionalBootTask } from "./game/boot-readiness";
import { LocaleProvider } from "./i18n/locale";
import { initializeGameOrientation } from "./game/screen-orientation";
import { prepareNativeContent, usesNativeContent } from "./game/native-content-update";
import { initializeConstrainedCompositor } from "./game/constrained-compositor";
import { initializeKeyboardViewport } from "./game/keyboard-viewport";
import "./components/keyboard-viewport.css";
import { initializeSafeArea } from "./game/safe-area";
import { installBackgroundReturn } from "./game/background-return";
import "./components/safe-area.css";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

// Native assets are versioned/verified by the Android updater. A browser service
// worker must not mix cached JS from a previous bundle into that immutable set.
initializeConstrainedCompositor();
const disposeSafeArea = initializeSafeArea();
if (import.meta.hot) import.meta.hot.dispose(disposeSafeArea);
const disposeKeyboardViewport = initializeKeyboardViewport();
if (import.meta.hot) import.meta.hot.dispose(disposeKeyboardViewport);
const disposeBackgroundReturn = installBackgroundReturn();
if (import.meta.hot) import.meta.hot.dispose(disposeBackgroundReturn);
const nativeContentReady = prepareNativeContent().then(() => prepareStartupUpdate());
const rootRouterImport = import("./RootRouter");
// Load native-content preparation and the application bundle in parallel. UI
// commit still waits for both, preserving the fail-closed readiness gate.
const routerModule = withOpeningAd(Promise.all([nativeContentReady, rootRouterImport]).then(([, module]) => module));
const RootRouter = lazy(() => routerModule.then((module) => ({ default: module.RootRouter })));
const readiness = createBootReadiness({
  interface: routerModule,
  orientation: optionalBootTask(initializeGameOrientation(), 1500),
  display: optionalBootTask(document.fonts?.ready ?? Promise.resolve(), 1500),
});

// Retire the old onboarding value; advertising uses a separate optional, neutral age-band preference.
try {
  localStorage.removeItem("varnax-age-band-v1");
} catch {
  // Storage restrictions must not prevent access to the game.
}

try {
  document.documentElement.dataset.graphics = localStorage.getItem("ea-graphics") ?? "auto";
  document.documentElement.classList.toggle("ea-reduced-motion", localStorage.getItem("ea-reduced-motion") === "true");
} catch { /* Private storage must not prevent startup. */ }
const syncPageVisibility = () => { document.documentElement.dataset.pageHidden = String(document.hidden); };
syncPageVisibility();
document.addEventListener("visibilitychange", syncPageVisibility);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LocaleProvider>
          <StartupBoundary readiness={readiness} onRetry={() => window.location.reload()}>
            <Suspense fallback={<StartupScreen />}><RootRouter /></Suspense>
          </StartupBoundary>
        </LocaleProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);

if (import.meta.env.PROD && !usesNativeContent() && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((registration) => registration.update())
      .catch(() => undefined);
  });
}
